import { Slot } from '@radix-ui/react-slot'
import { useLocation } from '@tanstack/react-router'
import { Menu, X } from 'lucide-react'
import { motion } from 'motion/react'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { Button, Skeleton } from '@/components'
import { cn } from '@/utils'

interface LayoutContextValue {
  isMobileMenuOpen: boolean
  setIsMobileMenuOpen: (open: boolean) => void
  isFullScreen: boolean
  setIsFullScreen: (fullScreen: boolean) => void
}

const LayoutContext = React.createContext<LayoutContextValue | undefined>(
  undefined,
)

const useLayout = () => {
  const context = React.useContext(LayoutContext)
  if (!context) {
    throw new Error('useLayout must be used within a Layout component')
  }
  return context
}

interface LayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  children: React.ReactNode
}

const Layout = ({ className, children, ...props }: LayoutProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)
  const [isFullScreen, setIsFullScreen] = React.useState(false)

  React.useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  const contextValue = React.useMemo(
    () => ({
      isMobileMenuOpen,
      setIsMobileMenuOpen,
      isFullScreen,
      setIsFullScreen,
    }),
    [isMobileMenuOpen, isFullScreen],
  )

  return (
    <LayoutContext.Provider value={contextValue}>
      <div
        className={cn(
          'flex h-full w-full overflow-hidden bg-background',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </LayoutContext.Provider>
  )
}
Layout.displayName = 'Layout'

interface SidebarProps extends React.HTMLAttributes<HTMLElement> {
  className?: string
  children: React.ReactNode
}

const Sidebar = ({ className, children, ...props }: SidebarProps) => {
  const { isMobileMenuOpen, setIsMobileMenuOpen } = useLayout()
  const menuButtonRef = React.useRef<HTMLButtonElement>(null)
  const sidebarRef = React.useRef<HTMLElement>(null)

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false)
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isMobileMenuOpen, setIsMobileMenuOpen])

  React.useEffect(() => {
    if (!isMobileMenuOpen) return

    const sidebar = sidebarRef.current
    if (!sidebar) return

    const focusableElements = sidebar.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement?.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement?.focus()
        }
      }
    }

    sidebar.addEventListener('keydown', handleTabKey)
    firstElement?.focus()

    return () => {
      sidebar.removeEventListener('keydown', handleTabKey)
      menuButtonRef.current?.focus()
    }
  }, [isMobileMenuOpen])

  return (
    <>
      <Button
        ref={menuButtonRef}
        theme="outline"
        isIcon
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-card border"
        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isMobileMenuOpen}
        aria-controls="sidebar-menu"
      >
        {isMobileMenuOpen ? (
          <X className="h-5 w-5" />
        ) : (
          <Menu className="h-5 w-5" />
        )}
      </Button>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        ref={sidebarRef}
        id="sidebar-menu"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 lg:w-48 xl:w-52 flex-col border-r bg-card text-card-foreground transition-transform duration-300 ease-in-out',
          'lg:relative lg:translate-x-0',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full',
          className,
        )}
        {...props}
      >
        {children}
      </aside>
    </>
  )
}
Sidebar.displayName = 'Sidebar'

interface SidebarHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  children: React.ReactNode
}

const SidebarHeader = ({
  className,
  children,
  ...props
}: SidebarHeaderProps) => {
  return (
    <div
      className={cn('flex h-14 shrink-0 items-center border-b px-4', className)}
      {...props}
    >
      {children}
    </div>
  )
}
SidebarHeader.displayName = 'SidebarHeader'

interface SidebarContentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  children: React.ReactNode
}

const SidebarContent = ({
  className,
  children,
  ...props
}: SidebarContentProps) => {
  return (
    <div className={cn('flex-1 overflow-y-auto py-4', className)} {...props}>
      {children}
    </div>
  )
}
SidebarContent.displayName = 'SidebarContent'

interface SidebarFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  children: React.ReactNode
}

const SidebarFooter = ({
  className,
  children,
  ...props
}: SidebarFooterProps) => {
  return (
    <div className={cn('shrink-0 border-t p-4', className)} {...props}>
      {children}
    </div>
  )
}
SidebarFooter.displayName = 'SidebarFooter'

interface SidebarItemProps extends React.HTMLAttributes<HTMLButtonElement> {
  className?: string
  icon?: React.ComponentType<{ className?: string }>
  children: React.ReactNode
  active?: boolean
  asChild?: boolean
}

const SidebarItem = React.forwardRef<HTMLButtonElement, SidebarItemProps>(
  ({ className, icon: Icon, children, active, asChild, ...props }, ref) => {
    const iconElement = Icon && (
      <Icon
        className={cn(
          'h-4 w-4 shrink-0 transition-colors',
          active
            ? 'text-brand'
            : 'text-muted-foreground group-hover:text-foreground',
        )}
      />
    )

    const activeIndicator = active && (
      <div className="ml-auto h-1.5 w-1.5 rounded-full bg-brand" />
    )

    const childContent =
      React.isValidElement(children) && asChild
        ? (children.props as any).children
        : children

    const content = (
      <>
        {iconElement}
        <span className="truncate">{childContent}</span>
        {activeIndicator}
      </>
    )

    if (asChild && React.isValidElement(children)) {
      return (
        <Slot
          ref={ref}
          data-active={active}
          className={cn(
            'group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors outline-none cursor-pointer',
            'focus-visible:ring-2 focus-visible:ring-brand/20 focus-visible:ring-offset-0',
            'text-blue-7',
            active
              ? 'bg-brand/10 font-semibold'
              : 'hover:bg-muted/50 hover:text-foreground',
            className,
          )}
          {...props}
        >
          {React.cloneElement(children, {}, content)}
        </Slot>
      )
    }

    return (
      <button
        ref={ref}
        data-active={active}
        className={cn(
          'group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors outline-none cursor-pointer',
          'focus-visible:ring-2 focus-visible:ring-brand/20 focus-visible:ring-offset-0',
          'text-blue-7',
          active
            ? 'bg-brand/10 font-semibold'
            : 'hover:bg-muted/50 hover:text-foreground',
          className,
        )}
        {...props}
      >
        {content}
      </button>
    )
  },
)
SidebarItem.displayName = 'SidebarItem'

interface MainContentProps extends React.HTMLAttributes<HTMLElement> {
  className?: string
  isLoading?: boolean
  skeleton?: React.ReactNode
  children: React.ReactNode
}

const MainContent = ({
  className,
  isLoading,
  skeleton,
  children,
  ...props
}: MainContentProps) => {
  const mainRef = React.useRef<HTMLElement>(null)
  const location = useLocation()

  React.useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [location.pathname])

  const defaultSkeleton = (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-[250px]" />
        <Skeleton className="h-4 w-[400px]" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-6 w-[200px]" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  )

  return (
    <main
      ref={mainRef}
      className={cn('flex-1 overflow-y-auto p-6 lg:ml-0', className)}
      {...props}
    >
      {isLoading ? skeleton || defaultSkeleton : children}
    </main>
  )
}
MainContent.displayName = 'MainContent'

interface FullScreenContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  children: React.ReactNode
  isActive?: boolean
}

const FullScreenContainer = ({
  className,
  children,
  isActive,
  ...props
}: FullScreenContainerProps) => {
  const layout = React.useContext(LayoutContext)
  const isFullScreen = isActive ?? layout?.isFullScreen ?? false

  React.useEffect(() => {
    if (!isFullScreen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isFullScreen])

  if (!isFullScreen) {
    return (
      <div className={className} {...props}>
        {children}
      </div>
    )
  }

  return createPortal(
    <motion.div
      key="fullscreen"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className={cn(
        'fixed inset-0 z-50 bg-background p-6 flex flex-col overflow-hidden',
        className,
      )}
    >
      {children}
    </motion.div>,
    document.body,
  )
}
FullScreenContainer.displayName = 'FullScreenContainer'

export {
  Layout,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarItem,
  MainContent,
  FullScreenContainer,
  useLayout,
}
